import { apiService } from './api';
import { Client, ApiResponse } from '../types';

export interface ClientFilters {
  search?: string;
  industry?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export const clientService = {
  async getClients(filters: ClientFilters = {}): Promise<{ clients: Client[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.industry) params.append('industry', filters.industry);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await apiService.get<ApiResponse<{ clients: Client[]; pagination: { page: number; limit: number; total: number; pages: number } }>>(`/clients?${params.toString()}`);
    if (response.data?.clients) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to fetch clients');
  },

  async getClient(id: number): Promise<Client> {
    const response = await apiService.get<ApiResponse<{ client: Client }>>(`/clients/${id}`);
    if (response.data?.client) {
      return response.data.client;
    }
    throw new Error(response.message || 'Failed to fetch client');
  },

  async createClient(data: Partial<Client>): Promise<Client> {
    const response = await apiService.post<ApiResponse<{ client: Client }>>('/clients', data);
    if (response.data?.client) {
      return response.data.client;
    }
    throw new Error(response.message || 'Failed to create client');
  },

  async updateClient(id: number, data: Partial<Client>): Promise<Client> {
    const response = await apiService.put<ApiResponse<{ client: Client }>>(`/clients/${id}`, data);
    if (response.data?.client) {
      return response.data.client;
    }
    throw new Error(response.message || 'Failed to update client');
  },

  async deleteClient(id: number): Promise<void> {
    const response = await apiService.delete<ApiResponse<void>>(`/clients/${id}`);
    if (response.message) {
      throw new Error(response.message);
    }
  },

  async getClientAssessments(clientId: number, filters: { page?: number; limit?: number; status?: string } = {}): Promise<{ assessments: any[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);

    const response = await apiService.get<ApiResponse<{ assessments: any[]; pagination: { page: number; limit: number; total: number; pages: number } }>>(`/clients/${clientId}/assessments?${params.toString()}`);
    if (response.data?.assessments) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to fetch client assessments');
  },

  async getClientUsers(clientId: number): Promise<any[]> {
    const response = await apiService.get<ApiResponse<{ clientUsers: any[] }>>(`/clients/${clientId}/users`);
    if (response.data?.clientUsers) {
      return response.data.clientUsers;
    }
    throw new Error(response.message || 'Failed to fetch client users');
  },

  async getClientUsersStats(): Promise<{ totalClientUsers: number }> {
    const response = await apiService.get<ApiResponse<{ totalClientUsers: number }>>(`/clients/users/stats`);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to fetch client users stats');
  },

  async inviteClientUser(clientId: number, data: { email: string; firstName: string; lastName: string; role?: string }): Promise<any> {
    const response = await apiService.post<ApiResponse<{ clientUser: any }>>(`/clients/${clientId}/users/invite`, data);
    if (response.data?.clientUser) {
      return response.data.clientUser;
    }
    throw new Error(response.message || 'Failed to invite client user');
  },

  // Fuzzy search clients (for advisors to find clients they don't have access to)
  async lookupClients(query: string): Promise<Client[]> {
    if (!query || query.length < 3) {
      return [];
    }
    try {
      // Cast to any to bypass strict type checking during robust parsing
      let response = await apiService.get<any>(`/clients/lookup?q=${encodeURIComponent(query)}`);

      // Handle string response (if not parsed by axios)
      if (typeof response === 'string') {
        try {
          response = JSON.parse(response);
        } catch (e) {
          console.error('Failed to parse response string', e);
        }
      }

      // Try multiple paths to find the clients array
      // Path 1: { data: { clients: [...] } } - Standard ApiResponse structure
      if (response?.data?.clients && Array.isArray(response.data.clients)) {
        return response.data.clients;
      }

      // Path 2: { clients: [...] } - Direct object
      if (response?.clients && Array.isArray(response.clients)) {
        return response.clients;
      }

      // Path 3: [...] - Direct array
      if (Array.isArray(response)) {
        return response;
      }

      // Path 4: { data: [...] } - Array inside data
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      }

      console.warn('Could not find clients array in response structure', response);
      return [];
    } catch (error) {
      console.error('Error looking up clients:', error);
      return [];
    }
  },

  // Attach advisor to a client
  async attachAdvisor(clientId: number): Promise<void> {
    const response = await apiService.post<ApiResponse<{ message: string }>>(`/clients/${clientId}/attach-advisor`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to attach to client');
    }
  },

  // Import clients from a CSV file
  async importClients(file: File): Promise<{
    summary: { total: number; created: number; existing: number; skipped: number };
    createdClients: Client[];
    existingClients: Client[];
  }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiService.post<ApiResponse<{
      summary: { total: number; created: number; existing: number; skipped: number };
      createdClients: Client[];
      existingClients: Client[];
    }>>('/clients/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    if (response.data) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to import clients');
  },
};

